import React from 'react';
import { useCompanyUsage, useCompany } from '../../hooks/useCompany';
import { formatLimit, isUnlimited, UNLIMITED } from '../../utils/planLimits';
import type { SubscriptionPlan } from '../../types';

const PLAN_DISPLAY: Record<SubscriptionPlan, { label: string; color: string }> = {
  starter: { label: 'Starter', color: 'bg-gray-100 text-gray-800' },
  business: { label: 'Business', color: 'bg-blue-100 text-blue-800' },
  enterprise: { label: 'Enterprise', color: 'bg-purple-100 text-purple-800' },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getBarColor(percent: number): string {
  if (percent > 80) return 'bg-red-500';
  if (percent > 60) return 'bg-yellow-500';
  return 'bg-green-500';
}

function UsageBar({ label, current, max, warning }: { label: string; current: number; max: number | null; warning: boolean }) {
  if (max === null || isUnlimited(max)) {
    return (
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{label}</span>
          <span className="font-medium text-gray-900">{current.toLocaleString('es-CL')} / {formatLimit(UNLIMITED)}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div className="bg-green-500 h-2.5 rounded-full" style={{ width: '5%' }} />
        </div>
      </div>
    );
  }

  const percent = max > 0 ? Math.min((current / max) * 100, 100) : 0;

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">
          {current.toLocaleString('es-CL')} / {max.toLocaleString('es-CL')}
          {warning && (
            <span className="ml-2 text-red-600" title="Uso superior al 80%">⚠</span>
          )}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={`${getBarColor(percent)} h-2.5 rounded-full transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

const PlanUsageWidget: React.FC<{ showPrice?: boolean }> = ({ showPrice = false }) => {
  const { data: company, isLoading: companyLoading } = useCompany();
  const { data: usage, isLoading: usageLoading } = useCompanyUsage();

  if (companyLoading || usageLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-2.5 bg-gray-200 rounded w-full mb-6" />
        <div className="h-2.5 bg-gray-200 rounded w-full" />
      </div>
    );
  }

  if (!company || !usage) return null;

  const { settings } = company;
  const planInfo = PLAN_DISPLAY[settings.subscriptionPlan];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Plan Actual</h3>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${planInfo.color}`}>
            {planInfo.label}
          </span>
        </div>
        {showPrice && (
          <span className="text-sm font-medium text-gray-600">
            {formatCurrency(settings.priceCLP)}/mes
          </span>
        )}
      </div>

      <div className="space-y-4">
        <UsageBar
          label="Usuarios activos"
          current={usage.activeUsers}
          max={usage.maxUsers}
          warning={usage.userWarning}
        />
        <UsageBar
          label="Gastos mensuales"
          current={usage.monthlyExpenses}
          max={usage.maxMonthlyExpenses ?? null}
          warning={usage.expenseWarning}
        />
      </div>

      {(usage.userWarning || usage.expenseWarning) && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">
            El uso de su plan supera el 80%. Considere actualizar su plan para evitar interrupciones.
          </p>
        </div>
      )}
    </div>
  );
};

export default PlanUsageWidget;
